/** Layout bits shared by the panels and dialogs: section titles, key-value rows, stat tiles, empty states, sub-tabs. */
import type { ReactNode } from 'react';
import { Tabs } from 'radix-ui';
import { cn } from '@/lib/cn';

export function SectionTitle({ children, right, className }: { children: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-1 flex items-center justify-between gap-2', className)}>
      <span className="text-[10px] uppercase tracking-wider text-ink-3">{children}</span>
      {right}
    </div>
  );
}

export function Row({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 py-1 text-xs', className)}>
      <span className="shrink-0 text-ink-3">{label}</span>
      <span className="min-w-0 text-right text-ink">{children}</span>
    </div>
  );
}

export function Card({ children, className, title, right }: { children: ReactNode; className?: string; title?: ReactNode; right?: ReactNode }) {
  return (
    <div className={cn('rounded-control border border-hairline bg-inset/60 px-3 py-2', className)}>
      {(title || right) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          {title && <span className="text-[10px] uppercase tracking-wider text-ink-3">{title}</span>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function Stat({ label, value, tone, className }: { label: string; value: ReactNode; tone?: 'warning' | 'caution' | 'nominal'; className?: string }) {
  return (
    <div className={cn('rounded-control border border-hairline bg-inset/60 px-3 py-2', className)}>
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{label}</div>
      <div className={cn('font-mono text-lg tabular text-ink', tone === 'warning' && 'text-warning', tone === 'caution' && 'text-caution', tone === 'nominal' && 'text-nominal')}>{value}</div>
    </div>
  );
}

export function EmptyState({ title, children, className }: { title: string; children?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1 rounded-control border border-dashed border-hairline px-3 py-3 text-xs', className)}>
      <div className="text-ink-2">{title}</div>
      {children && <div className="leading-snug text-ink-3">{children}</div>}
    </div>
  );
}

export const inputClass =
  'rounded-control border border-hairline bg-inset px-2.5 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none disabled:opacity-60';

export const subTabTrigger =
  'whitespace-nowrap rounded-control px-2 py-1 text-[11px] text-ink-3 transition hover:text-ink data-[state=active]:bg-overlay data-[state=active]:text-ink disabled:cursor-not-allowed disabled:opacity-40';

/** Pill sub-tabs inside a panel (Readings, Graph, Prediction, Correlation and the spec sheet tabs). */
export function SubTabs({ tabs, value, onChange, children, className }: { tabs: Array<{ key: string; label: string; disabled?: boolean; title?: string }>; value: string; onChange: (v: string) => void; children: ReactNode; className?: string }) {
  return (
    <Tabs.Root value={value} onValueChange={onChange} className={cn('flex min-h-0 flex-col', className)}>
      <Tabs.List className="flex shrink-0 flex-wrap gap-1 rounded-control border border-hairline bg-inset/40 p-0.5">
        {tabs.map((t) => (
          <Tabs.Trigger key={t.key} value={t.key} disabled={t.disabled} title={t.title} className={subTabTrigger}>
            {t.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {children}
    </Tabs.Root>
  );
}

export function SubTabContent({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <Tabs.Content value={value} className={cn('mt-2 min-h-0 outline-none', className)}>
      {children}
    </Tabs.Content>
  );
}

export function Button({ children, className, primary, danger, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { primary?: boolean; danger?: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-control px-2.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40',
        primary ? 'bg-accent text-accent-ink hover:brightness-95' : danger ? 'border border-warning/40 text-warning hover:bg-warning/10' : 'border border-hairline-strong bg-raised text-ink hover:bg-overlay',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
